import { useLayoutEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Icon } from './Icon';
import type { ReactNode } from 'react';

/**
 * Pane — the docked panel (deskkit.pane, §13.5).
 *
 * Law 4: THE PANE IS AN EXTENSION OF THE SELECTION, never a second window. It
 * shows what the selected row is; it does not host an unrelated task. If you
 * want a second window, you want a Modal.
 *
 * Responsive (README §5): docked beside the content at desk width, a full sheet
 * on the phone — it cannot dock beside a 375px column. Either way it keeps ONE
 * exit row. Reproducing the phone's pinned head AND foot at desk width is
 * explicitly the defect, not the fix (§12.3).
 *
 * §13.13, and this is what lint-desktop.mjs exists to catch: nothing in the
 * main column may sit under the pane. The pane paints OVER an overflowing row
 * rather than clipping it, so a hidden control looks absent rather than broken.
 * Sixteen shipped that way, Save buttons among them. kit-extras.css wraps rows
 * and clips `.dmain` as the backstop.
 */

/**
 * A DOCKED PANE IS A 360px COLUMN THE APP'S OWN FLOATING CONTROLS DO NOT KNOW
 * ABOUT — and the assistant launcher sits in the same bottom-right corner as
 * the pane's foot, which is where Save and Deactivate live. It covered them.
 *
 * Whether the pane is docked beside the content or covering it as a phone
 * sheet is decided by a CONTAINER query, so no media query can answer it from
 * outside. This reads the answer the cascade actually produced — `position`
 * resolves to `sticky` only in the docked rule — and publishes it on `body`,
 * where one stylesheet rule shifts the launcher clear. It says `sheet` rather
 * than nothing when covering, so the two states are distinguishable from an
 * unmounted pane.
 */
function usePaneDockFlag() {
  const ref = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const docked = getComputedStyle(el).position === 'sticky';
      document.body.dataset.maiePane = docked ? 'docked' : 'sheet';
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => {
      ro.disconnect();
      delete document.body.dataset.maiePane;
    };
  }, []);

  return ref;
}

interface PaneProps {
  head?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  /** Phone only — the sheet's single exit. At desk width the pane is a column. */
  onClose?: () => void;
  className?: string;
}

export function Pane({ head, children, foot, onClose, className }: PaneProps) {
  const ref = usePaneDockFlag();
  return (
    <aside ref={ref} className={clsx('pane', className)} aria-label="Details">
      {/* A pane with no head draws NO head strip. An empty `.pane-head` is a
          blank bordered box above the message, which is what the empty-selection
          pane used to ship. */}
      {head || onClose ? (
        <div className="pane-head">
          {head}
          {onClose ? (
            <button
              type="button"
              className="kit-pane-close"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon name="x" />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="pane-body">{children}</div>

      {foot ? <div className="pane-foot">{foot}</div> : null}
    </aside>
  );
}

/**
 * PaneHead — overline, title, optional sub (deskkit.pane_head).
 *
 * The overline names WHAT KIND of thing is selected ("PATIENT", "BILL"), which
 * is how the pane stays legible as an extension of the selection rather than a
 * free-floating panel.
 */
export function PaneHead({
  overline,
  title,
  sub,
  wrapTitle,
}: {
  overline: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  /**
   * Let a title take two lines.
   *
   * Every data title on these boards is one line ("CBC — Ahmed H."), so the
   * kit clamps. A few panes are headed with a sentence — f10g's "Yours or
   * MAIE's — one line, every state" — which the board sets on two.
   */
  wrapTitle?: boolean;
}) {
  return (
    <div>
      <p className="t-ovl mut">{overline}</p>
      <p className={clsx('t-title', wrapTitle && 'kit-pane-title-wrap')} style={{ marginTop: 2 }}>
        {title}
      </p>
      {sub ? <p className="t-sec mut">{sub}</p> : null}
    </div>
  );
}

/**
 * EmptyPane — what the pane says before anything is selected
 * (deskkit.empty_pane).
 *
 * The default copy states Law 4 in the UI itself, which is deliberate: the one
 * place a user learns the panel's contract is the moment it has nothing to show.
 */
export function EmptyPane({
  text = 'Pick a row — this panel is always an extension of what you select.',
}: {
  text?: string;
}) {
  return (
    <Pane>
      <div className="emptystate">
        <p className="t-sec">{text}</p>
      </div>
    </Pane>
  );
}
